(() => {
  const SELECTORS = {
    product: '[data-iconic-product]',
    information: '[data-iconic-information]',
    customizer: '[data-iconic-customizer]',
    lpo: '[data-iconic-lpo]',
    review: '[data-iconic-review]',
    summary: '[data-iconic-summary]',
    setupAlert: '[data-iconic-setup-alert]',
    previousButton: '[data-iconic-previous]',
    nextButton: '[data-iconic-next]',
    purchaseActions: '[data-iconic-purchase-actions]',
    approval: '[data-iconic-approval]',
    stepInstructions: '[data-iconic-step-instructions]',
    stepButton: '[data-iconic-step-button]',
    marker: '[data-iconic-step-marker]',
    lpoOption: '.cl-po--option[data-option]'
  };

  function normalize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function titleCase(value) {
    return String(value || '')
      .replace(/[-_]+/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase())
      .trim();
  }

  function isElement(value) {
    return value instanceof HTMLElement;
  }

  function getPreviewPosition(marker, key) {
    const rawPosition =
      marker.dataset.iconicPreviewPosition ||
      marker.getAttribute('data-iconic-preview-position');

    if (rawPosition !== null && rawPosition !== undefined && rawPosition !== '') {
      const previewPosition = Number(rawPosition);

      if (Number.isInteger(previewPosition) && previewPosition > 0) {
        return previewPosition;
      }

      console.warn(
        `Iconic configurator: Invalid preview position "${rawPosition}" for step "${key}".`
      );
    }

    /* Preserve the existing Raglan mapping until its markers are configured. */
    if (key === 'front') return 1;
    if (key === 'back') return 5;

    return null;
  }

  function getDynamicPreviewPosition(option, key) {
    const optionName = option?.getAttribute('data-option') || '';
    const explicitPosition =
      option?.dataset.iconicPreviewPosition ||
      option?.getAttribute('data-iconic-preview-position');
    const nameMatch = optionName.match(
      /\[(?:preview|view)(?:-position)?\s*[:=]\s*(\d+)\]/i
    );
    const rawPosition = explicitPosition || nameMatch?.[1] || '';
    const previewPosition = Number(rawPosition);

    if (Number.isInteger(previewPosition) && previewPosition > 0) {
      return previewPosition;
    }

    console.warn(
      `Iconic configurator: Add [preview:N] to the internal LPO name for the "${key}" view.`
    );

    return null;
  }

  function getLowestCommonAncestor(elements, boundary) {
    if (!elements.length) return null;

    let ancestor = elements[0].parentElement;

    while (ancestor && ancestor !== boundary.parentElement) {
      const containsAll = elements.every((element) => ancestor.contains(element));

      if (containsAll) {
        return ancestor;
      }

      ancestor = ancestor.parentElement;
    }

    return boundary;
  }

  function getDirectChildContaining(parent, element) {
    if (!parent || !element) return null;

    let current = element;

    while (current && current.parentElement !== parent) {
      current = current.parentElement;
    }

    return current?.parentElement === parent ? current : null;
  }

  function fieldLabel(field, lpo) {
    if (!field || !lpo) return '';

    if (field.id && window.CSS?.escape) {
      const explicitLabel = lpo.querySelector(
        `label[for="${CSS.escape(field.id)}"]`
      );

      if (explicitLabel) {
        return explicitLabel.textContent.trim();
      }
    }

    const wrappingLabel = field.closest('label');

    if (wrappingLabel) {
      return wrappingLabel.textContent.trim();
    }

    return (
      field.getAttribute('aria-label') ||
      field.getAttribute('data-name') ||
      field.name?.replace(/^properties\[|\]$/g, '') ||
      ''
    ).trim();
  }

  function fieldValue(field) {
    if (!field || field.disabled) return '';

    if (field.type === 'hidden') return '';

    if (field.type === 'file') {
      if (!field.files?.length) return '';

      return Array.from(field.files)
        .map((file) => file.name)
        .join(', ');
    }

    if (field.type === 'checkbox' || field.type === 'radio') {
  if (!field.checked) return '';

  if (field.type === 'checkbox') {
    return field.value && field.value !== 'on'
      ? field.value
      : 'Selected';
  }

  const wrappingLabel = field.closest('label');

  return (
    field.value ||
    wrappingLabel?.textContent.trim() ||
    'Selected'
  );
}

    if (field.tagName === 'SELECT') {
      const selectedOption = field.selectedOptions?.[0];

      if (!selectedOption || !selectedOption.value) return '';

      return selectedOption.textContent.trim();
    }

    return String(field.value || '').trim();
  }

  function initIconicProduct(root) {
    if (!root || root.dataset.iconicInitialized === 'true') return;

    root.dataset.iconicInitialized = 'true';

    const information = root.querySelector(SELECTORS.information);
    const customizer = root.querySelector(SELECTORS.customizer);
    const lpo = root.querySelector(SELECTORS.lpo);
    const review = root.querySelector(SELECTORS.review);
    const summary = root.querySelector(SELECTORS.summary);
    const setupAlert = root.querySelector(SELECTORS.setupAlert);
    const previousButton = root.querySelector(SELECTORS.previousButton);
    const nextButton = root.querySelector(SELECTORS.nextButton);
    const purchaseActions = root.querySelector(SELECTORS.purchaseActions);
    const approval = root.querySelector(SELECTORS.approval);
    const stepInstructions = root.querySelector(SELECTORS.stepInstructions);
    const submitButton = purchaseActions?.querySelector('[name="add"]');
    const variantDataElement = root.nextElementSibling?.matches(
      '[data-iconic-variant-data]'
    )
      ? root.nextElementSibling
      : document.querySelector('[data-iconic-variant-data]');

    let variantData = null;

    try {
      variantData = variantDataElement
        ? JSON.parse(variantDataElement.textContent)
        : null;
    } catch (error) {
      console.warn(
        'Iconic configurator: Shopify variant data could not be read.',
        error
      );
    }

    const customizerRequested =
      new URLSearchParams(window.location.search).get('customize') === 'true';

    if (!lpo) {
      console.warn('Iconic configurator: LPO container was not found.');
      return;
    }

    let steps = [];
    let currentStepIndex = 0;
    let highestReachedIndex = 0;
    let groupingReady = false;
    let groupingParent = null;
    let observer = null;
    let rebuildTimer = null;
    let progressContainer = null;
    let markerSignature = '';
    let activeViewIndex = null;
    let activeViewLockInterval = null;
    let customizationActionNumber = 0;
    const customizationActions = [];
    const actionByField = new WeakMap();

    function summaryLabel(field) {
      let label =
        field.getAttribute('data-name') ||
        field.name?.replace(/^properties\[|\]$/g, '') ||
        fieldLabel(field, lpo) ||
        'Selection';

      return label
        .replace(/\s+/g, ' ')
        .replace(/^Choose\s+/i, '')
        .replace(/_/g, ' ')
        .trim();
    }

    function storedUpload(field) {
      if (!field) return null;

      if (field.type === 'file' && field.files?.length) {
        const file = field.files[0];

        return {
          filename: file.name || 'Uploaded image',
          imageUrl: URL.createObjectURL(file)
        };
      }

      const label = summaryLabel(field);
      const rawValue = String(field.value || '').trim();

      if (!/\b(?:image|artwork|upload|logo|photo)\b/i.test(label) || !rawValue) {
        return null;
      }

      const urlMatch = rawValue.match(
        /(?:https?:\/\/|blob:|data:image\/)[^\s"'<>]+/i
      );
      const imageUrl = urlMatch?.[0] || '';
      let filename = '';

      if (imageUrl && !imageUrl.startsWith('data:')) {
        try {
          filename = decodeURIComponent(
            new URL(imageUrl, window.location.href).pathname.split('/').pop() || ''
          ).replace(/[?#].*$/, '');
        } catch (error) {
          filename = '';
        }
      }

      if (!filename && !/^\[object\s/i.test(rawValue) && rawValue.length <= 180) {
        filename = rawValue;
      }

      return {
        filename: filename || 'Uploaded image',
        imageUrl
      };
    }

    function isBaseProductSelection(label) {
      const key = normalize(label);

      return (
        key === 'size' ||
        key === 'quantity' ||
        key === 'color' ||
        key === 'colour'
      );
    }

    function chronologicalActionLabel(label) {
      const baseLabel = label.replace(/\s+\d+$/i, '').trim();
      const shouldNumber = /\b(?:technique|text|image)$/i.test(baseLabel);

      if (!shouldNumber) return label;

      const occurrence =
        customizationActions.filter((action) =>
          action.label.startsWith(`${baseLabel} `)
        ).length + 1;

      return `${baseLabel} ${occurrence}`;
    }

    function recordCustomizationAction(event) {
      const field = event.target;

      if (
        !(
          field instanceof HTMLInputElement ||
          field instanceof HTMLSelectElement ||
          field instanceof HTMLTextAreaElement
        ) ||
        field.disabled ||
        field.type === 'hidden'
      ) {
        return;
      }

      let label = summaryLabel(field);

      /*
       * LPO's native upload input is generically named "file". Rename that
       * action using the active product view at the instant the customer
       * selects it, preserving both its identity and chronological position.
       */
      if (field.type === 'file' && normalize(label) === 'file') {
        const activeStep = steps[currentStepIndex];

        if (activeStep && activeStep.key !== 'review') {
          label = `${activeStep.label} Image`;
        }
      }

      if (isBaseProductSelection(label)) return;

      const value = fieldValue(field);
      const existingAction = actionByField.get(field);

      if (!value) {
        if (existingAction) existingAction.removed = true;
        return;
      }

      let imageUrl = '';

      if (field.type === 'file' && field.files?.[0]) {
        imageUrl = URL.createObjectURL(field.files[0]);
      }

      if (existingAction) {
        existingAction.value = value;
        existingAction.imageUrl = imageUrl || existingAction.imageUrl;
        existingAction.removed = false;
        return;
      }

      const action = {
        order: ++customizationActionNumber,
        label: chronologicalActionLabel(label),
        value,
        imageUrl,
        removed: false
      };

      customizationActions.push(action);
      actionByField.set(field, action);
    }

    lpo.addEventListener('input', recordCustomizationAction, true);
    lpo.addEventListener('change', recordCustomizationAction, true);

    function getSelectedVariantOption(optionName) {
      const escapedName = window.CSS?.escape
        ? CSS.escape(`v_${optionName}`)
        : `v_${optionName}`;
      const fields = Array.from(
        lpo.querySelectorAll(`[name="${escapedName}"]`)
      );
      const selectedField = fields.find((field) => {
        if (field.type === 'radio' || field.type === 'checkbox') {
          return field.checked;
        }

        return Boolean(field.value);
      });

      return selectedField ? String(selectedField.value || '').trim() : '';
    }

    function syncShopifyVariant() {
      const optionNames = variantData?.optionNames;
      const variants = variantData?.variants;

      if (!Array.isArray(optionNames) || !Array.isArray(variants)) return null;

      const selectedOptions = optionNames.map(getSelectedVariantOption);

      if (selectedOptions.some((value) => !value)) return null;

      const selectedVariant = variants.find((variant) =>
        selectedOptions.every(
          (value, index) => String(variant.options?.[index] || '') === value
        )
      );

      if (!selectedVariant) return null;

      root.querySelectorAll('input[name="id"]').forEach((input) => {
        if (String(input.value) === String(selectedVariant.id)) return;

        input.value = selectedVariant.id;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      const variantUrl = new URL(window.location.href);
      variantUrl.searchParams.set('variant', selectedVariant.id);
      window.history.replaceState({}, '', variantUrl);

      const detail = { variant: selectedVariant };
      document.dispatchEvent(
        new CustomEvent('variant:change', { bubbles: true, detail })
      );
      document.dispatchEvent(
        new CustomEvent('product:variant-change', { bubbles: true, detail })
      );

      return selectedVariant;
    }

    lpo.addEventListener(
      'change',
      (event) => {
        const field = event.target;

        if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) {
          return;
        }

        if (!String(field.name || '').startsWith('v_')) return;

        /*
         * LPO's Overlay Values layers are keyed to the complete Shopify
         * variant. Keep the real product variant synchronized with LPO's
         * imported Size/Color controls before its preview redraw finishes.
         */
        syncShopifyVariant();
        window.requestAnimationFrame(syncShopifyVariant);
        window.setTimeout(syncShopifyVariant, 100);
      },
      true
    );

    function getExistingProgressContainer() {
      const existingButtons = Array.from(
        root.querySelectorAll(SELECTORS.stepButton)
      );

      if (!existingButtons.length) return null;

      return existingButtons[0].parentElement;
    }

    function getMarkerData(marker, index) {
      const rawKey =
        marker.dataset.iconicStepMarker ||
        marker.getAttribute('data-iconic-step-marker') ||
        `step-${index + 1}`;

      const rawLabel =
        marker.dataset.iconicStepLabel ||
        marker.getAttribute('data-iconic-step-label') ||
        rawKey;

      const key = normalize(rawKey) || `step-${index + 1}`;

      return {
        key,
        label: titleCase(rawLabel) || `Step ${index + 1}`,
        previewPosition: getPreviewPosition(marker, key),
        marker,
        groupElements: []
      };
    }

    function clearPreviousGrouping() {
  lpo
    .querySelectorAll('[data-iconic-dynamic-step]')
    .forEach((element) => {
      element.classList.remove('iconic-dynamic-step--inactive');
      delete element.dataset.iconicDynamicStep;
    });
}

    function discoverSteps() {
      clearPreviousGrouping();

      const markers = Array.from(
        lpo.querySelectorAll(SELECTORS.marker)
      ).filter(isElement);
      markerSignature = getMarkerSignature();
      if (!markers.length) {
        const lpoOptions = Array.from(
          lpo.querySelectorAll(SELECTORS.lpoOption)
        ).filter(isElement);

        if (!lpoOptions.length) {
          groupingReady = false;

          if (setupAlert) {
            setupAlert.hidden = false;
            setupAlert.textContent =
              'Live Product Options is still loading. Please wait.';
          }

          return false;
        }

        const sizeAndQuantity = [];
        const colorOptions = [];
        const viewSteps = [];

        lpoOptions.forEach((option) => {
          const internalName = option.getAttribute('data-option') || '';
          const cleanName = internalName
            .replace(/\[(?:preview|view)(?:-position)?\s*[:=]\s*\d+\]/gi, '')
            .trim();
          const key = normalize(cleanName);

          if (key === 'size' || key === 'quantity') {
            sizeAndQuantity.push(option);
            return;
          }

          if (key === 'color' || key === 'colour') {
            colorOptions.push(option);
            return;
          }

          if (!/design[\s-]+elements$/i.test(cleanName)) return;

          const viewLabel = cleanName
            .replace(/\s*design[\s-]+elements$/i, '')
            .trim();
          const viewKey = normalize(viewLabel);

          if (!viewKey) return;

          viewSteps.push({
            key: viewKey,
            label: titleCase(viewLabel),
            previewPosition: getDynamicPreviewPosition(option, viewKey),
            marker: null,
            groupElements: [option]
          });
        });

        const discoveredSteps = [];

        if (sizeAndQuantity.length) {
          discoveredSteps.push({
            key: 'size-and-quantity',
            label: 'Size & Quantity',
            previewPosition: null,
            marker: null,
            groupElements: sizeAndQuantity
          });
        }

        if (colorOptions.length) {
          discoveredSteps.push({
            key: 'color',
            label: 'Color',
            previewPosition: null,
            marker: null,
            groupElements: colorOptions
          });
        }

        discoveredSteps.push(...viewSteps);

        if (!viewSteps.length) {
          groupingReady = false;

          if (setupAlert) {
            setupAlert.hidden = false;
            setupAlert.textContent =
              'No LPO Dynamic Options groups ending in "Design Elements" were found.';
          }

          return false;
        }

        discoveredSteps.forEach((step) => {
          step.groupElements.forEach((element) => {
            element.dataset.iconicDynamicStep = step.key;
          });
        });

        steps = [
          ...discoveredSteps,
          {
            key: 'review',
            label: 'Review',
            previewPosition: null,
            marker: null,
            groupElements: []
          }
        ];

        groupingParent = lpo.querySelector('.cl-po--options') || lpo;
        groupingReady = true;

        if (setupAlert) {
          setupAlert.hidden = true;
        }

        buildProgressButtons();

        return true;
      }

      const discoveredSteps = markers.map(getMarkerData);

      groupingParent = getLowestCommonAncestor(markers, lpo);

      if (!groupingParent) {
        groupingReady = false;

        if (setupAlert) {
          setupAlert.hidden = false;
          setupAlert.textContent =
            'The Live Product Options workflow markers could not be grouped.';
        }

        return false;
      }

      const markerChildren = discoveredSteps.map((step) =>
        getDirectChildContaining(groupingParent, step.marker)
      );

      if (markerChildren.some((element) => !element)) {
        groupingReady = false;

        if (setupAlert) {
          setupAlert.hidden = false;
          setupAlert.textContent =
            'One or more Live Product Options markers could not be located inside the options structure.';
        }

        return false;
      }

      const allChildren = Array.from(groupingParent.children);
      const markerIndexes = markerChildren.map((element) =>
        allChildren.indexOf(element)
      );

      if (
        markerIndexes.some((index) => index < 0) ||
        markerIndexes.some(
          (index, position) =>
            position > 0 && index <= markerIndexes[position - 1]
        )
      ) {
        groupingReady = false;

        if (setupAlert) {
          setupAlert.hidden = false;
          setupAlert.textContent =
            'The Iconic workflow markers are not in the correct order inside Live Product Options.';
        }

        return false;
      }

      discoveredSteps.forEach((step, index) => {
        const startIndex = markerIndexes[index];
        const endIndex =
          index + 1 < markerIndexes.length
            ? markerIndexes[index + 1]
            : allChildren.length;

        step.groupElements = allChildren.slice(startIndex, endIndex);

        step.groupElements.forEach((element) => {
          element.dataset.iconicDynamicStep = step.key;
        });
      });

      steps = [
        ...discoveredSteps,
        {
          key: 'review',
          label: 'Review',
          marker: null,
          groupElements: []
        }
      ];

      groupingReady = true;

      if (setupAlert) {
        setupAlert.hidden = true;
      }

      buildProgressButtons();

      return true;
    }

    function buildProgressButtons() {
      progressContainer =
        progressContainer || getExistingProgressContainer();

      if (!progressContainer || !steps.length) return;

      progressContainer.replaceChildren();

      steps.forEach((step, index) => {
        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'iconic-configurator__step';
        button.dataset.iconicStepButton = step.key;
        button.disabled = index > highestReachedIndex;

        const number = document.createElement('span');
        number.className = 'iconic-configurator__step-number';
        number.textContent =
          step.key === 'review' ? String(index + 1) : String(index + 1);

        const label = document.createElement('span');
        label.className = 'iconic-configurator__step-label';
        label.textContent = step.label;

        button.append(number, label);

        button.addEventListener('click', () => {
          if (!button.disabled) {
            showStep(index);
          }
        });

        progressContainer.append(button);
      });
    }

    function getStepButtons() {
      return Array.from(
        root.querySelectorAll(SELECTORS.stepButton)
      );
    }

    function getFieldsForStep(step) {
      if (!step || step.key === 'review') return [];

      return step.groupElements.flatMap((element) =>
        Array.from(
          element.querySelectorAll('input, select, textarea')
        )
      );
    }

    function validateStep(step) {
      if (!groupingReady || !step || step.key === 'review') {
        return true;
      }

      const fields = getFieldsForStep(step).filter(
        (field) =>
          !field.disabled &&
          field.offsetParent !== null
      );

      const requiredFields = fields.filter(
        (field) =>
          field.required ||
          field.getAttribute('aria-required') === 'true'
      );

      const processedGroups = new Set();

      for (const field of requiredFields) {
        if (
          (field.type === 'radio' || field.type === 'checkbox') &&
          field.name
        ) {
          if (processedGroups.has(field.name)) continue;

          processedGroups.add(field.name);

          const groupFields = fields.filter(
            (candidate) => candidate.name === field.name
          );

          if (!groupFields.some((candidate) => candidate.checked)) {
            field.focus();
            field.reportValidity?.();
            return false;
          }

          continue;
        }

        if (!field.checkValidity()) {
          field.focus();
          field.reportValidity();
          return false;
        }
      }

      return true;
    }

    function buildSummary() {
      if (!summary) return;

      summary.replaceChildren();

      const seen = new Set();

      function appendSummaryRow(label, value, imageUrl = '') {
        const signature = `${label}:${value}`;

        if (!value || seen.has(signature)) return;

        seen.add(signature);

        const term = document.createElement('dt');
        const detail = document.createElement('dd');

        term.textContent = label;

        if (imageUrl) {
          const image = document.createElement('img');
          const filename = document.createElement('span');

          image.src = imageUrl;
          image.alt = value;
          image.className = 'iconic-review__uploaded-image';
          filename.textContent = value;
          detail.append(image, filename);
          detail.classList.add('iconic-review__uploaded-file');
        } else {
          detail.textContent = value;
        }

        summary.append(term, detail);
      }

      /* Always place the basic product selections first. */
      lpo
        .querySelectorAll('input, select, textarea')
        .forEach((field) => {
          if (
            field.name === 'id' ||
            field.type === 'hidden' ||
            field.disabled
          ) {
            return;
          }

          const value = fieldValue(field);

          if (!value) return;

          const label = summaryLabel(field);

          if (!isBaseProductSelection(label)) return;

          appendSummaryRow(label, value);
        });

      /*
       * Dynamic Options can renumber and reorder its generated fields.
       * Use the interaction history so techniques, text and uploads appear
       * in the same sequence in which the customer added them.
       */
      customizationActions
        .filter((action) => !action.removed)
        .sort((first, second) => first.order - second.order)
        .forEach((action) => {
          appendSummaryRow(action.label, action.value, action.imageUrl);
        });

      /*
       * LPO replaces a completed file input with a generated value after the
       * upload finishes. Include those stored upload fields even when other
       * text and technique actions were already captured chronologically.
       */
      lpo
        .querySelectorAll('input, select, textarea')
        .forEach((field) => {
          if (actionByField.has(field)) return;

          const upload = storedUpload(field);

          if (upload) {
            const uploadLabel = summaryLabel(field);
            const matchingRecordedUpload = customizationActions.some(
              (action) =>
                !action.removed &&
                Boolean(action.imageUrl) &&
                normalize(action.label) === normalize(uploadLabel)
            );

            /* LPO's generated upload value duplicates the native file event. */
            if (matchingRecordedUpload) return;

            appendSummaryRow(
              uploadLabel,
              upload.filename,
              upload.imageUrl
            );
            return;
          }

          /* Fallback for ordinary values restored before any action was seen. */
          if (customizationActions.length) return;

          if (
            field.name === 'id' ||
            field.type === 'hidden' ||
            field.disabled
          ) {
            return;
          }

          const value = fieldValue(field);

          if (!value) return;

          const label = summaryLabel(field);

          if (isBaseProductSelection(label)) return;

            appendSummaryRow(label, value);
          });

      if (!summary.children.length) {
        const term = document.createElement('dt');
        const detail = document.createElement('dd');

        term.textContent = 'Customization';
        detail.textContent =
          'Your selected options will be attached to the product when it is added to the cart.';

        summary.append(term, detail);
      }
    }

    function updatePurchaseState() {
      const onReviewStep =
        steps[currentStepIndex]?.key === 'review';

      if (purchaseActions) {
        purchaseActions.hidden = !onReviewStep;
      }

      if (!submitButton) return;

      const approvalComplete = approval
        ? approval.checked
        : true;

      submitButton.disabled = !(
        onReviewStep &&
        approvalComplete
      );
    }

    function updateNextButton() {
      if (!nextButton || !steps.length) return;

      const currentStep = steps[currentStepIndex];
      const nextStep = steps[currentStepIndex + 1];

      if (!nextStep) {
        nextButton.hidden = true;
        return;
      }

      nextButton.hidden = false;

      if (nextStep.key === 'review') {
        nextButton.textContent = 'Continue to Review';
      } else {
        nextButton.textContent =
          `Continue to Step ${currentStepIndex + 2}: ${nextStep.label}`;
      }
    }

    function getLpoGallery() {
  return window.Cloudlift?.options?.App?.products?.[0]
    ?._gallery?._splideMain;
}

function stopActiveViewLock() {
  if (activeViewLockInterval !== null) {
    window.clearInterval(activeViewLockInterval);
    activeViewLockInterval = null;
  }
}

function enforceActiveView() {
  if (
    activeViewIndex === null ||
    activeViewIndex === undefined
  ) {
    return false;
  }

  const activeStep = steps[currentStepIndex];

  if (
    !activeStep ||
    activeStep.previewPosition === null ||
    activeStep.previewPosition === undefined
  ) {
    return false;
  }

  const gallery = getLpoGallery();

  if (!gallery || typeof gallery.go !== 'function') {
    return false;
  }

  if (gallery.index !== activeViewIndex) {
    gallery.go(activeViewIndex);
  }

  return true;
}

function startActiveViewLock() {
  stopActiveViewLock();

  if (
    activeViewIndex === null ||
    activeViewIndex === undefined
  ) {
    return;
  }

  /*
   * Keep the current LPO product view locked for the entire
   * active customization step. This corrects later preview
   * resets caused by conditional options, text fields, uploads
   * and LPO preview rerenders.
   */
  enforceActiveView();

  activeViewLockInterval = window.setInterval(() => {
    enforceActiveView();
  }, 100);
}

function activateLpoPreview(step) {
  if (!step || step.key === 'review') return;

  const previewPosition = step.previewPosition;

  if (previewPosition === null || previewPosition === undefined) {
    activeViewIndex = null;
    stopActiveViewLock();
    delete root.dataset.iconicActiveViewIndex;

    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });

    return;
  }

  /* LPO preview positions are one-based; Splide gallery indexes are zero-based. */
  stopActiveViewLock();
  activeViewIndex = previewPosition - 1;
  root.dataset.iconicActiveViewIndex = String(activeViewIndex);

  const product = window.Cloudlift?.options?.App?.products?.[0];
  const gallery = product?._gallery;

  if (!gallery || typeof gallery.selectPreview !== 'function') {
    return;
  }

  const keepCorrectPreviewActive = () => {
    gallery.selectPreview(previewPosition);
    startActiveViewLock();
  };

  keepCorrectPreviewActive();

  window.requestAnimationFrame(() => {
    keepCorrectPreviewActive();
    window.dispatchEvent(new Event('resize'));
  });

  window.setTimeout(keepCorrectPreviewActive, 100);
  window.setTimeout(keepCorrectPreviewActive, 300);
  window.setTimeout(keepCorrectPreviewActive, 600);

  document.dispatchEvent(
    new CustomEvent('iconic:lpo-step-change', {
      bubbles: true,
      detail: {
        key: step.key,
        label: step.label,
        index: currentStepIndex,
        previewPosition
      }
    })
  );
}

function enforceActiveProductView(event) {
  const activeStep = steps[currentStepIndex];

  if (!activeStep) return;

  if (
    activeStep.previewPosition === null ||
    activeStep.previewPosition === undefined
  ) {
    return;
  }

  const target = event.target;

  if (!(target instanceof HTMLElement)) return;

  if (!lpo.contains(target)) return;

  window.setTimeout(() => {
    activateLpoPreview(activeStep);
  }, 50);
}

lpo.addEventListener('change', enforceActiveProductView, true);
lpo.addEventListener('input', enforceActiveProductView, true);
lpo.addEventListener('click', enforceActiveProductView, true);

    function showStep(
  index,
  shouldScroll = true,
  allowDelayedRefresh = true
) {
  if (!groupingReady || !steps.length) return;

  const safeIndex = Math.max(
    0,
    Math.min(index, steps.length - 1)
  );

  currentStepIndex = safeIndex;

  highestReachedIndex = Math.max(
    highestReachedIndex,
    currentStepIndex
  );

  const activeStep = steps[currentStepIndex];

  if (stepInstructions) {
    const isProductView =
      activeStep.key !== 'review' &&
      activeStep.previewPosition !== null &&
      activeStep.previewPosition !== undefined;

    stepInstructions.classList.toggle(
      'iconic-step-instructions--important',
      isProductView
    );

    if (activeStep.key === 'review') {
      stepInstructions.textContent =
        'Please review your selections below. To unlock the Add to Cart button, read and acknowledge the review confirmation.';
    } else if (isProductView) {
      stepInstructions.textContent =
        'Before adding text or an image, select the technique you want used for that customization. If you want multiple techniques, click “+ Choose A Technique” and select a technique before you select and add each customization. If only one technique is selected, we will assume it applies to all customizations for that product view.';
    } else {
      stepInstructions.textContent =
        'Complete each step. Your selections and customization remain saved as you move through the product views.';
    }
  }

  lpo
    .querySelectorAll('[data-iconic-dynamic-step]')
    .forEach((element) => {
      const belongsToActiveStep =
        element.dataset.iconicDynamicStep === activeStep.key;

      const shouldHide =
        activeStep.key === 'review' ||
        !belongsToActiveStep;

      element.classList.toggle(
        'iconic-dynamic-step--inactive',
        shouldHide
      );
    });

  const isReviewStep = activeStep.key === 'review';

if (review) {
  review.hidden = !isReviewStep;
}

/*
 * Hide the complete Live Product Options form during Review.
 * This prevents the Back-step controls from remaining visible
 * above the review summary.
 */
if (lpo) {
  lpo.hidden = isReviewStep;
}

if (isReviewStep) {
  activeViewIndex = null;
  stopActiveViewLock();
  delete root.dataset.iconicActiveViewIndex;
  buildSummary();
}

  if (previousButton) {
    previousButton.hidden = currentStepIndex === 0;
  }

  updateNextButton();

  getStepButtons().forEach((button, buttonIndex) => {
    const isActive = buttonIndex === currentStepIndex;

    button.disabled =
      buttonIndex > highestReachedIndex;

    if (isActive) {
      button.setAttribute('aria-current', 'step');
    } else {
      button.removeAttribute('aria-current');
    }
  });

  updatePurchaseState();

  if (activeStep.key !== 'review') {
    activateLpoPreview(activeStep);
  }

  /*
   * LPO adds and removes conditional controls after the customer
   * enters a step. Re-detect the marker boundaries after those
   * controls finish rendering so controls from the previous view
   * do not remain visible.
   */
  if (allowDelayedRefresh && activeStep.key !== 'review') {
    const activeKey = activeStep.key;

    window.setTimeout(() => {
      const preservedIndex = steps.findIndex(
        (step) => step.key === activeKey
      );

      if (!discoverSteps()) return;

      const refreshedIndex = steps.findIndex(
        (step) => step.key === activeKey
      );

      currentStepIndex =
        refreshedIndex >= 0
          ? refreshedIndex
          : Math.max(preservedIndex, 0);

      showStep(
        currentStepIndex,
        false,
        false
      );
    }, 350);
  }

  if (shouldScroll) {
    root.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
}
function getMarkerSignature() {
  const markerValue = Array.from(
    lpo.querySelectorAll(SELECTORS.marker)
  )
    .map((marker) => {
      const key =
        marker.dataset.iconicStepMarker ||
        marker.getAttribute('data-iconic-step-marker') ||
        '';

      const label =
        marker.dataset.iconicStepLabel ||
        marker.getAttribute('data-iconic-step-label') ||
        '';

      const previewPosition =
        marker.dataset.iconicPreviewPosition ||
        marker.getAttribute('data-iconic-preview-position') ||
        '';

      return `${normalize(key)}:${normalize(label)}:${previewPosition}`;
    })
    .join('|');

  if (markerValue) return `markers:${markerValue}`;

  const optionValue = Array.from(
    lpo.querySelectorAll(SELECTORS.lpoOption)
  )
    .map((option) => option.getAttribute('data-option') || '')
    .join('|');

  return `options:${optionValue}`;
}
    function rebuildWorkflow() {
      const activeKey = steps[currentStepIndex]?.key;

      if (!discoverSteps()) return;

      const preservedIndex = steps.findIndex(
        (step) => step.key === activeKey
      );

      if (preservedIndex >= 0) {
        currentStepIndex = preservedIndex;
      } else {
        currentStepIndex = 0;
      }

      highestReachedIndex = Math.max(
        highestReachedIndex,
        currentStepIndex
      );

      showStep(currentStepIndex, false);
    }

    previousButton?.addEventListener('click', () => {
      showStep(currentStepIndex - 1);
    });

    nextButton?.addEventListener('click', () => {
      const currentStep = steps[currentStepIndex];

      if (!validateStep(currentStep)) return;

      showStep(currentStepIndex + 1);
    });

    approval?.addEventListener(
      'change',
      updatePurchaseState
    );

/*
 * LPO may return to the first preview when the customer clicks
 * Add a Custom Image, Add a Custom Text, selects a technique,
 * uploads a file, types text or changes another conditional option.
 *
 * These listeners restore the preview assigned to the active
 * workflow step after LPO finishes processing the interaction.
 */
  [
  'click',
  'change',
  'input',
  'focusin'
].forEach((eventName) => {
  lpo.addEventListener(
    eventName,
    () => {
      const activeStep = steps[currentStepIndex];

      if (
        activeViewIndex === null ||
        !activeStep ||
        activeStep.previewPosition === null ||
        activeStep.previewPosition === undefined
      ) {
        return;
      }

      /*
       * LPO may reconstruct conditional controls after any
       * interaction. Restart the lock so the assigned product
       * view remains active throughout that update.
       */
      startActiveViewLock();
    },
    true
  );
});

if (customizerRequested) {
      if (information) information.hidden = true;
      if (customizer) customizer.hidden = false;

      const startWorkflow = () => {
        if (discoverSteps()) {
          showStep(0, false);
          return true;
        }

        return false;
      };

      if (!startWorkflow()) {
        let attempts = 0;

        const loadingInterval = window.setInterval(() => {
          attempts += 1;

          if (startWorkflow() || attempts >= 40) {
            window.clearInterval(loadingInterval);
          }
        }, 250);
      }

      observer = new MutationObserver(() => {
  window.clearTimeout(rebuildTimer);

  rebuildTimer = window.setTimeout(() => {
    const newSignature = getMarkerSignature();

    /*
     * LPO changes its DOM while text fields, uploads,
     * conditional controls and previews are being updated.
     * Only rebuild the workflow when the actual marker
     * structure changes.
     */
    if (newSignature !== markerSignature) {
      rebuildWorkflow();
    }
  }, 300);
});

      observer.observe(lpo, {
        childList: true,
        subtree: true
      });
    } else {
      if (information) information.hidden = false;
      if (customizer) customizer.hidden = true;
    }

    root.classList.add('is-ready');
  }

  function initializeAll(scope = document) {
    scope
      .querySelectorAll(SELECTORS.product)
      .forEach(initIconicProduct);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAll()
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => initializeAll(event.target)
  );
})();
